import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { Header } from '@/components/shared';
import OrganizationLogo from '@/components/shared/OrganizationLogo';
import { TabNavigation } from '@/components/shared';
import { getOrganizationNameFromTeam } from '@/utils/organizationUtils';
import { getApiUrl } from '@/config/api';
import { OverviewTab, CodesTab, MembersTab } from '.';
import Select from 'react-select';
import { generateCommands } from '@/config/commands';

/**
 * Interface for paginated API responses
 */
interface PaginatedResponse<T> {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

/**
 * Interface for team member information
 */
interface TeamMember {
  id: number;
  username: string;
  email: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  whoop_connected?: boolean;
}

/**
 * Interface for team data
 */
interface TeamData {
  id: number;
  name: string;
  description?: string;
  organization: {
    id: number;
    name: string;
    description?: string;
    created_at: string;
    updated_at: string;
  };
  created_at: string;
  coaches: TeamMember[];
  athletes: TeamMember[];
  coach_code: string;
  athlete_code: string;
}

/**
 * Interface for user information
 */
interface UserInfo {
  username: string;
  role?: string;
  full_name?: string;
  first_name?: string;
}

/**
 * Simple Team Information component with tabbed interface for better organization.
 * Provides easy access to team overview, join codes, and member lists.
 */
const TeamInfo: React.FC = () => {
  const [teamData, setTeamData] = useState<TeamData[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string>('');
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'codes' | 'members'>('overview');
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Team options for react-select
  const teamOptions = Array.isArray(teamData) ? teamData.map(team => ({
    value: team.id,
    label: `${team.name} (${team.athletes.length} athletes)`
  })) : [];

  // Themed styles for react-select
  const selectStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: isDark ? '#232946' : '#fff',
      borderColor: isDark ? '#3B3B4F' : '#E5E7EB',
      color: isDark ? '#A1A1AA' : '#000000',
      borderRadius: '1rem',
      minHeight: '44px',
      boxShadow: state.isFocused || state.menuIsOpen ? (isDark ? '0 0 0 2px #7C3AED' : '0 0 0 2px #6366F1') : 'none',
      transition: 'transform 0.15s cubic-bezier(0.4,0,0.2,1)',
      transform: state.isFocused || state.menuIsOpen ? 'scale(1.03)' : 'scale(1)',
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: isDark ? '#232946' : '#fff',
      borderRadius: '1rem',
      boxShadow: isDark ? '0 8px 32px 0 #00000080' : '0 8px 32px 0 #6366F120',
      border: isDark ? '1px solid #3B3B4F' : '1px solid #E5E7EB',
      marginTop: 6,
      zIndex: 30,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: 'transparent',
      ':active': {
        ...provided[':active'],
        backgroundColor: 'transparent',
      },
      ':hover': {
        ...provided[':hover'],
        backgroundColor: isDark ? '#334155' : '#E0F2FE',
      },
      color: 'var(--text-primary)',
      borderRadius: '0.75rem',
      fontWeight: state.isSelected ? 700 : 500,
      fontSize: '1rem',
      padding: '10px 16px',
      cursor: 'pointer',
      transition: 'transform 0.15s cubic-bezier(0.4,0,0.2,1)',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'var(--text-secondary)',
      fontWeight: 400,
      fontSize: '1rem',
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'var(--text-secondary)',
      fontWeight: 500,
      fontSize: '1rem',
    }),
    dropdownIndicator: (provided: any, state: any) => ({
      ...provided,
      color: isDark ? '#A1A1AA' : '#6B7280',
      transition: 'color 0.2s',
      ':hover': {
        color: isDark ? '#fff' : '#232946',
      },
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      backgroundColor: isDark ? '#393A5A' : '#E5E7EB',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'var(--text-secondary)',
      fontSize: '1rem',
    }),
  };

  /**
   * Fetch teams data for the current user
   */
  const fetchTeamData = async (): Promise<void> => {
    const token = localStorage.getItem('access');
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/api/teams/teams/'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('access');
          localStorage.removeItem('refresh');
          navigate('/login');
          return;
        }
        throw new Error(`Error fetching teams: ${response.status}`);
      }

      const teamsApiResponse: any = await response.json();

      // Handle different response formats:
      // 1. Direct array: [team1, team2, ...]
      // 2. Paginated response: { results: [...], count: X, next: ..., previous: ... }
      // 3. Data wrapper: { data: [team1, team2, ...] }
      const teams: TeamData[] = Array.isArray(teamsApiResponse)
        ? teamsApiResponse
        : (teamsApiResponse.results || teamsApiResponse.data || []);

      // Validate that teams is an array
      if (!Array.isArray(teams)) {
        console.error('Teams API response is not in expected format:', teamsApiResponse);
        throw new Error('Invalid teams data format received from API');
      }

      console.log(`TeamInformation: Fetched ${teams.length} teams from API`);

      setTeamData(teams);

      // Auto-select first team if available
      if (teams.length > 0) {
        setSelectedTeam(teams[0]);
      }
    } catch (err) {
      console.error('Error in fetchTeamData:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Generate commands for command palette (after fetchTeamData is defined)
  const commands = useMemo(() => generateCommands({
    navigate,
    setTeamInfoTab: (tab: string) => setActiveTab(tab as 'overview' | 'codes' | 'members'),
    fetchTeamData
  }), [navigate]);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('access');
    if (!token) {
      navigate('/login');
      return;
    }

    // Get user info from localStorage
    const userInfoString = localStorage.getItem('user_info');
    if (userInfoString) {
      try {
        const user: UserInfo = JSON.parse(userInfoString);
        setUserInfo(user);
      } catch (err) {
        console.error('Error parsing user info:', err);
      }
    }

    fetchTeamData();
  }, [navigate]);

  /**
   * Copy code to clipboard with elegant feedback
   */
  const copyToClipboard = async (code: string, type: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(`${type}-${code}`);
      setTimeout(() => setCopiedCode(''), 3000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  /**
   * Get organization name from team data
   */
  const getOrganizationName = (team: TeamData): string => {
    return team.organization.name || 'Team';
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[var(--bg-primary)] via-[var(--bg-gradient-mid)] to-purple-50 dark:to-purple-900">
        <Header
          userInfo={userInfo}
          isDark={isDark}
          title="Team Information"
          showThemeToggle={true}
          commands={commands}
        />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center space-y-4">
            <div className="relative">
              <div className={`w-20 h-20 border-4 ${isDark ? 'border-purple-800' : 'border-purple-200'} rounded-full animate-spin`}>
                <div className={`absolute top-0 left-0 w-20 h-20 border-4 border-transparent ${isDark ? 'border-t-purple-400' : 'border-t-purple-600'} rounded-full animate-spin`}></div>
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold cgm-text-primary">Loading Team Information</h3>
              <p className="cgm-text-secondary">Getting team details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[var(--bg-primary)] via-[var(--bg-gradient-mid)] to-purple-50 dark:to-purple-900">
        <Header
          userInfo={userInfo}
          isDark={isDark}
          title="Team Information"
          showThemeToggle={true}
          commands={commands}
        />
        <div className="flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl p-8 text-center`}>
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className={`w-8 h-8 ${isDark ? 'text-red-400' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'} mb-4`}>Something went wrong</h2>
              <p className="cgm-text-secondary mb-6 leading-relaxed">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-3 rounded-xl font-medium hover:from-purple-700 hover:to-purple-800 transition-all duration-200 transform hover:scale-105"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No teams state
  if (!Array.isArray(teamData) || teamData.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[var(--bg-primary)] via-[var(--bg-gradient-mid)] to-purple-50 dark:to-purple-900">
        <Header
          userInfo={userInfo}
          isDark={isDark}
          title="Team Information"
          showThemeToggle={true}
          commands={commands}
        />

        <div className="max-w-4xl mx-auto p-6 mt-20">
          <div className="text-center">
            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold cgm-text-primary mb-4">No Teams Found</h2>
            <p className="text-lg cgm-text-secondary mb-8 max-w-2xl mx-auto leading-relaxed">
              You haven't been assigned to any teams yet. Contact your administrator to get started.
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-200 transform hover:scale-105"
            >
              <span>📊</span>
              <span>Go to Dashboard</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--bg-primary)] via-[var(--bg-gradient-mid)] to-purple-50 dark:to-black">
      {/* Header */}
      <Header
        userInfo={userInfo}
        isDark={isDark}
        title="Team Information"
        subtitle={selectedTeam ? `${getOrganizationName(selectedTeam)} • ${selectedTeam.name}` : undefined}
        logo={
          selectedTeam ? (
            <OrganizationLogo
              organizationName={getOrganizationNameFromTeam(selectedTeam)}
              isDark={isDark}
              size="medium"
              className="opacity-90"
            />
          ) : undefined
        }
        showThemeToggle={true}
        commands={commands}
      />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto p-6">
        {/* Team Selection */}
        <div className="mb-8">
          <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="space-y-3">
                <h2 className="text-2xl font-bold cgm-text-primary">Team Information</h2>
                <p className="cgm-text-secondary">View team details, codes, and members</p>
              </div>
              <div className="lg:min-w-[300px]">
                <Select
                  value={teamOptions.find(option => option.value === selectedTeam?.id)}
                  onChange={(option) => {
                    if (option) {
                      const team = teamData.find(t => t.id === option.value);
                      if (team) setSelectedTeam(team);
                    }
                  }}
                  options={teamOptions}
                  classNamePrefix="react-select"
                  placeholder="Choose a team..."
                  menuPortalTarget={typeof window !== 'undefined' ? window.document.body : undefined}
                  styles={selectStyles}
                />
              </div>
            </div>
          </div>
        </div>

        {selectedTeam && (
          <>
            {/* Tab Navigation */}
            <TabNavigation
              tabs={[
                { id: 'overview', label: 'Overview', icon: '📊' },
                { id: 'codes', label: 'Join Codes', icon: '🔑' },
                { id: 'members', label: 'Members', icon: '👥' }
              ]}
              activeTab={activeTab}
              onTabChange={(tabId) => setActiveTab(tabId as any)}
            />

            {/* Tab Content */}
            <div className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-lg rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl min-h-[500px]`}>

              {activeTab === 'overview' && (
                <OverviewTab selectedTeam={selectedTeam} isDark={isDark} formatDate={formatDate} />
              )}
              {activeTab === 'codes' && (
                <CodesTab selectedTeam={selectedTeam} isDark={isDark} copiedCode={copiedCode} copyToClipboard={copyToClipboard} />
              )}
              {activeTab === 'members' && (
                <MembersTab selectedTeam={selectedTeam} isDark={isDark} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TeamInfo;